import { AddUserPayload, EditUserPayload, UserRole } from '../../../../types';
import { IUserFileds } from './UserModal';

/**
 * Builds the union of role-owned relation fields for the user's selected roles. Each role contributes only
 * the relations it owns, so a Manager-Submitter ({OP Manager, Operating Partner}) sends BOTH the managed
 * submitters (OP Manager) AND the submitter data (workflow / managers / deals / proxy / dates / country).
 * The backend treats `roles` as the single source of truth (the legacy role/isOp/isAdmin columns were
 * dropped in Phase 5 / migration M3).
 */
const buildRelationPayload = ({
  roles,
  managers,
  svpManagers,
  workflow,
  operatingPartnerDeals,
  operatingPartners,
  dealOwnerDeals,
  proxyOperatingPartners,
  proxyCanSubmitFor,
  proxy,
  startDate,
  endDate,
  country,
}: IUserFileds): Partial<AddUserPayload & EditUserPayload> => {
  const has = (role: UserRole) => roles.includes(role);
  const payload: Partial<AddUserPayload & EditUserPayload> = {};

  // Operating Partner (Submitter): owns the approval workflow, its approvers and the submitter profile.
  if (has(UserRole.OPERATING_PARTNER)) {
    payload.workflowId = workflow?.id ? Number(workflow.id) : undefined;
    payload.svpManagers = (svpManagers || []).map((svpManager) => Number(svpManager.id));
    payload.operatingPartnerManagers = (managers || []).map((manager) => Number(manager.id));
    payload.operatingPartnerDeals = (operatingPartnerDeals || []).map((deal) => Number(deal.id));
    payload.proxy = Number(proxy?.id);
    payload.startDate = startDate;
    payload.endDate = endDate;
    payload.country = country;
  }

  // OP Manager: owns the set of submitters they manage.
  if (has(UserRole.OP_MANAGER)) {
    payload.operatingPartners = (operatingPartners || []).map((partner) => Number(partner.id));
  }

  // Proxy: owns the set of operating partners they submit on behalf of, and (SVP-820) which of those
  // they may SUBMIT for rather than only fill. Always sent alongside the assignment so the backend can
  // treat the switch list as authoritative and revoke anyone left out of it.
  if (has(UserRole.PROXY)) {
    const assignedIds = (proxyOperatingPartners || []).map((partner) => Number(partner.id));
    payload.proxyOperatingPartners = assignedIds;
    payload.proxyCanSubmitFor = (proxyCanSubmitFor || []).filter((id) => assignedIds.includes(id));
  }

  // Deal Owner: owns the set of deals they approve.
  if (has(UserRole.DEAL_OWNER)) {
    payload.dealOwnerDeals = (dealOwnerDeals || []).map((deal) => Number(deal.id));
  }

  return payload;
};

export const createAddUserPayload = (values: IUserFileds): AddUserPayload => {
  const { name, email, roles } = values;
  return {
    name,
    email,
    roles,
    ...buildRelationPayload(values),
  };
};

export const createEditUserPayload = (values: IUserFileds): EditUserPayload => {
  const { roles } = values;
  return {
    roles,
    ...buildRelationPayload(values),
  };
};
